import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import {
  Box,
  Flex,
  Text,
  Button,
  Spinner,
  Image,
  VStack,
  HStack,
  IconButton,
  Icon,
  Card,
  CardBody,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];

interface CoverImageUploadProps {
  /** Current cover image URL (for preview when editing) */
  currentImageUrl?: string | null;
  /** Called after successful upload with the new URL */
  onUpload: (file: Blob) => Promise<void>;
  /** Called when user removes the cover image */
  onRemove?: () => Promise<void>;
  /** Whether upload is in progress */
  isUploading?: boolean;
}

/**
 * Crops the selected area from the image source and returns a Blob.
 */
async function getCroppedBlob(imageSrc: string, crop: Area): Promise<Blob> {
  const image = new window.Image();
  image.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = reject;
    image.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
      'image/png',
    );
  });
}

export function CoverImageUpload({
  currentImageUrl,
  onUpload,
  onRemove,
  isUploading,
}: CoverImageUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t('admin.playlists.coverImage.invalidFormat', 'Unsupported image format. Please use JPEG, PNG, WebP, GIF, BMP, or TIFF.'));
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }, [t]);

  const handleConfirmCrop = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      await onUpload(croppedBlob);
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch {
      setError(t('admin.playlists.coverImage.uploadFailed', 'Failed to upload cover image.'));
    }
  }, [imageSrc, croppedAreaPixels, onUpload, t]);

  const handleCancelCrop = useCallback(() => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  // Crop dialog
  if (imageSrc) {
    return (
      <Card data-testid="cover-image-crop-dialog">
        <CardBody>
          <VStack spacing={3}>
          <Text fontWeight="bold" fontSize="sm">
            {t('admin.playlists.coverImage.cropTitle', 'Crop Cover Image')}
          </Text>
          <Box
            w="100%"
            h={300}
            borderRadius="16px"
            overflow="hidden"
            position="relative"
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
          <HStack spacing={2} justify="flex-end" w="100%">
            <Button
              size="md"
              variant="outline"
              borderRadius="16px"
              onClick={handleCancelCrop}
              data-testid="btn-cancel-crop"
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="md"
              colorScheme="brand"
              borderRadius="16px"
              onClick={handleConfirmCrop}
              isDisabled={isUploading}
              data-testid="btn-confirm-crop"
            >
              {isUploading ? <Spinner size="sm" /> : t('admin.playlists.coverImage.upload', 'Upload')}
            </Button>
          </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <VStack spacing={2} data-testid="cover-image-upload">
      {currentImageUrl ? (
        <Box position="relative" role="group" w={120} h={120}>
          <Image
            src={currentImageUrl}
            w={120}
            h={120}
            borderRadius="16px"
            data-testid="cover-image-preview"
            objectFit="cover"
          />
          {/* Overlay with change/remove icons */}
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            gap={2}
            bg="blackAlpha.600"
            borderRadius="16px"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.15s"
          >
            <IconButton
              aria-label={t('admin.playlists.coverImage.change', 'Change Image')}
              icon={<Icon viewBox="0 0 24 24" w={5} h={5} color="white"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.33a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" /></Icon>}
              size="sm"
              variant="ghost"
              _hover={{ bg: 'whiteAlpha.300' }}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={isUploading}
              data-testid="btn-select-cover-image"
            />
            {onRemove && (
              <IconButton
                aria-label={t('common.delete')}
                icon={<Icon viewBox="0 0 24 24" w={5} h={5} color="white"><path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></Icon>}
                size="sm"
                variant="ghost"
                colorScheme="red"
                _hover={{ bg: 'whiteAlpha.300' }}
                onClick={onRemove}
                isDisabled={isUploading}
                data-testid="btn-remove-cover"
              />
            )}
          </Flex>
        </Box>
      ) : (
        <Button
          size="md"
          variant="outline"
          borderRadius="16px"
          onClick={() => fileInputRef.current?.click()}
          isDisabled={isUploading}
          data-testid="btn-select-cover-image"
        >
          {t('admin.playlists.coverImage.select', 'Select Image')}
        </Button>
      )}

      {error && <Text color="red.500" fontSize="xs">{error}</Text>}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="cover-image-file-input"
      />
      <Text fontSize="xs" color={secondaryColor}>
        {t('admin.playlists.coverImage.hint', 'Square crop recommended. Supported: JPEG, PNG, WebP, GIF, BMP, TIFF. Max 20 MB.')}
      </Text>
    </VStack>
  );
}
